import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAllCandidates, getUserProfile, getCompanyById, getJobPostsByEmployer, deleteJobPost } from '../services/api';

const EmployerDashboard = () => {
  const { currentUser } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [profile, setProfile] = useState(null);
  const [company, setCompany] = useState(null);
  const [jobPosts, setJobPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      // Load employer profile
      const profileResult = await getUserProfile(currentUser.uid);
      if (profileResult.success && profileResult.profile) {
        setProfile(profileResult.profile);

        // Load company details if profile has companyId
        if (profileResult.profile.companyId) {
          const companyResult = await getCompanyById(profileResult.profile.companyId);
          if (companyResult.success) {
            setCompany(companyResult.company);
          }
        }
      }

      // Load candidates
      const candidatesResult = await getAllCandidates();
      if (candidatesResult.success) {
        setCandidates(candidatesResult.candidates.slice(0, 6));
      }

      // Load job posts
      const jobPostsResult = await getJobPostsByEmployer(currentUser.uid);
      if (jobPostsResult.success) {
        setJobPosts(jobPostsResult.jobPosts);
      }

      setLoading(false);
    };

    loadData();
  }, [currentUser]);

  const handleDeleteJobPost = async (jobPostId) => {
    if (window.confirm('Are you sure you want to delete this job post?')) {
      const result = await deleteJobPost(jobPostId);
      if (result.success) {
        setJobPosts(jobPosts.filter(jp => jp.id !== jobPostId));
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-teal-50 to-blue-50">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-bounce">💼</div>
          <div className="text-2xl font-bold text-gray-700">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-teal-50 to-blue-50 py-8">
      <div className="container mx-auto px-4">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-green-600 via-teal-600 to-cyan-600 rounded-2xl shadow-2xl p-8 mb-8 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-10 rounded-full -ml-24 -mb-24"></div>
          <div className="relative z-10 flex items-center justify-between">
            <div>
              <div className="flex items-center mb-3">
                <span className="text-5xl mr-4">💼</span>
                <h1 className="text-4xl font-bold">
                  Welcome back, {currentUser.displayName || 'Employer'}!
                </h1>
              </div>
              <p className="text-green-100 text-lg ml-16">
                {company ? `Recruiting for ${company.name}` : 'Manage your hiring process'}
              </p>
            </div>
            <div className="hidden md:block text-8xl opacity-20">
              🎯
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium mb-1">Active Jobs</p>
                <p className="text-4xl font-bold">
                  {company?.openPositions || 0}
                </p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-full p-4">
                <div className="text-4xl">💼</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium mb-1">Applications</p>
                <p className="text-4xl font-bold">47</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-full p-4">
                <div className="text-4xl">📋</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium mb-1">Interviews</p>
                <p className="text-4xl font-bold">12</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-full p-4">
                <div className="text-4xl">🤝</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl shadow-xl p-6 text-white transform hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100 text-sm font-medium mb-1">Candidates</p>
                <p className="text-4xl font-bold">{candidates.length}</p>
              </div>
              <div className="bg-white bg-opacity-20 rounded-full p-4">
                <div className="text-4xl">👥</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Top Candidates */}
            <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-3 mr-4">
                    <span className="text-3xl">👥</span>
                  </div>
                  <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                    Top Candidates
                  </h2>
                </div>
                <Link to="/candidates" className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-4 py-2 rounded-lg hover:from-blue-600 hover:to-indigo-600 font-semibold shadow-md hover:shadow-lg transform hover:scale-105 transition-all">
                  View All →
                </Link>
              </div>

              {candidates.length === 0 ? (
                <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-purple-50 rounded-xl border-2 border-dashed border-gray-300">
                  <div className="text-6xl mb-3">👥</div>
                  <p className="text-gray-500 text-lg font-medium">No candidates available</p>
                </div>
              ) : (
                <div className="space-y-5">
                  {candidates.map((candidate) => (
                    <div
                      key={candidate.id}
                      className="border-2 border-gray-200 rounded-2xl p-6 hover:border-purple-400 hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] bg-gradient-to-br from-white to-purple-50"
                    >
                      <div className="flex items-start space-x-4">
                        <img
                          src={candidate.profileImage}
                          alt={candidate.name}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-800">
                            {candidate.name}
                          </h3>
                          <p className="text-blue-600">{candidate.title}</p>
                          <p className="text-sm text-gray-600">{candidate.location}</p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {candidate.skills && candidate.skills.slice(0, 3).map((skill, idx) => (
                              <span
                                key={idx}
                                className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                        <Link
                          to={`/candidate/${candidate.id}`}
                          className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 text-sm font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all whitespace-nowrap"
                        >
                          👁️ View Profile
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* My Job Posts */}
            <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-3 mr-4">
                    <span className="text-3xl">📝</span>
                  </div>
                  <h2 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                    My Job Posts
                  </h2>
                </div>
                <Link
                  to="/post-job"
                  className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-5 py-3 rounded-xl hover:from-green-700 hover:to-emerald-700 font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
                >
                  ✨ Post New Job
                </Link>
              </div>

              {jobPosts.length === 0 ? (
                <div className="text-center py-12 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border-2 border-dashed border-green-300">
                  <div className="text-6xl mb-4">📝</div>
                  <p className="text-gray-700 font-bold text-lg mb-2">No job posts yet</p>
                  <p className="text-sm text-gray-600 mb-6">Create your first job posting to attract candidates</p>
                  <Link
                    to="/post-job"
                    className="inline-block bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-3 rounded-xl hover:from-green-700 hover:to-emerald-700 font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
                  >
                    ✨ Post a Job
                  </Link>
                </div>
              ) : (
                <div className="space-y-5">
                  {jobPosts.map((job) => (
                    <div
                      key={job.id}
                      className="border-2 border-gray-200 rounded-2xl p-6 hover:border-green-400 hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.01] bg-gradient-to-br from-white to-green-50"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-800">
                            {job.position}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {job.customCompanyName || job.companyName}
                          </p>
                        </div>
                        <span className={`px-4 py-2 rounded-full text-xs font-bold shadow-md ${
                          job.status === 'approved'
                            ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white'
                            : job.status === 'rejected'
                            ? 'bg-gradient-to-r from-red-500 to-pink-600 text-white'
                            : 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white'
                        }`}>
                          {job.status === 'approved' ? '✓ Approved' : job.status === 'rejected' ? '✗ Rejected' : '⏳ Pending'}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 text-sm">
                        <div className="flex items-center text-gray-600">
                          <span className="mr-1">📍</span>
                          <span>{job.location}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <span className="mr-1">💰</span>
                          <span>{job.salary}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <span className="mr-1">👥</span>
                          <span>{job.vacancies} {job.vacancies === 1 ? 'vacancy' : 'vacancies'}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <span className="mr-1">📅</span>
                          <span>{job.postedDate}</span>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 mb-3">
                        {job.requiredSkills && job.requiredSkills.slice(0, 4).map((skill, idx) => (
                          <span
                            key={idx}
                            className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>

                      <button
                        onClick={() => handleDeleteJobPost(job.id)}
                        className="bg-gradient-to-r from-red-500 to-pink-600 text-white px-4 py-2 rounded-lg hover:from-red-600 hover:to-pink-700 text-sm font-bold shadow-md hover:shadow-lg transform hover:scale-105 transition-all"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-3 mr-3">
                  <span className="text-2xl">⚡</span>
                </div>
                <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  Quick Actions
                </h3>
              </div>
              <div className="space-y-3">
                <Link
                  to="/post-job"
                  className="block w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white text-center py-3 rounded-xl hover:from-green-600 hover:to-emerald-600 font-semibold shadow-md hover:shadow-lg transform hover:scale-105 transition-all"
                >
                  ✨ Post New Job
                </Link>
                <Link
                  to="/profile/employer"
                  className="block w-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-center py-3 rounded-xl hover:from-blue-600 hover:to-indigo-600 font-semibold shadow-md hover:shadow-lg transform hover:scale-105 transition-all"
                >
                  💼 Update Profile
                </Link>
                <Link
                  to="/settings"
                  className="block w-full bg-gradient-to-r from-gray-500 to-gray-600 text-white text-center py-3 rounded-xl hover:from-gray-600 hover:to-gray-700 font-semibold shadow-md hover:shadow-lg transform hover:scale-105 transition-all"
                >
                  ⚙️ Settings
                </Link>
              </div>
            </div>

            {/* Hiring Pipeline */}
            <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-xl p-3 mr-3">
                  <span className="text-2xl">📊</span>
                </div>
                <h3 className="text-xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                  Hiring Pipeline
                </h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 p-3 rounded-xl border-l-4 border-blue-500">
                  <span className="text-gray-700 font-medium">📋 New Applications</span>
                  <span className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 py-1 rounded-full text-sm font-bold shadow-md">
                    23
                  </span>
                </div>
                <div className="flex items-center justify-between bg-gradient-to-r from-yellow-50 to-orange-50 p-3 rounded-xl border-l-4 border-yellow-500">
                  <span className="text-gray-700 font-medium">🔍 Screening</span>
                  <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-4 py-1 rounded-full text-sm font-bold shadow-md">
                    15
                  </span>
                </div>
                <div className="flex items-center justify-between bg-gradient-to-r from-purple-50 to-pink-50 p-3 rounded-xl border-l-4 border-purple-500">
                  <span className="text-gray-700 font-medium">🤝 Interviews</span>
                  <span className="bg-gradient-to-r from-purple-500 to-pink-600 text-white px-4 py-1 rounded-full text-sm font-bold shadow-md">
                    9
                  </span>
                </div>
                <div className="flex items-center justify-between bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-xl border-l-4 border-green-500">
                  <span className="text-gray-700 font-medium">✓ Offers</span>
                  <span className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-1 rounded-full text-sm font-bold shadow-md">
                    3
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployerDashboard;
